import type { ReactNode, ReactElement } from 'react';
import { useEffectiveRole } from './useRole';
import { PERMISSIONS, type Permission } from './permissions';
import type { Role } from './roles';
import { RouteNotFound } from './RouteNotFound';
import useUserStore from '../../store/user';

type Common = { fallback?: ReactNode; loading?: ReactNode; children: ReactNode };
type RequireRoleProps =
  | (Common & { oneOf: readonly Role[]; permission?: never })
  | (Common & { permission: Permission; oneOf?: never });

export function RequireRole(props: RequireRoleProps): ReactElement {
  const { children, fallback = <RouteNotFound />, loading = null } = props;
  const role = useEffectiveRole();
  const user = useUserStore((s) => s.user);

  // `role === null` covers both an unresolved user query (render `loading`, don't flash 404) and a
  // loaded user whose role string isn't in the known set (fail closed to `fallback`).
  if (user === null) return <>{loading}</>;
  if (role === null) return <>{fallback}</>;

  const allowedRoles: readonly string[] =
    'oneOf' in props && props.oneOf ? props.oneOf : PERMISSIONS[(props as { permission: Permission }).permission];

  return <>{allowedRoles.includes(role) ? children : fallback}</>;
}

export default RequireRole;
